import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Hero from '../components/Hero';
import CategoryFilter from '../components/CategoryFilter';
import TraderCard from '../components/TraderCard';
import TraderDetail from '../components/TraderDetail';
import { traderService, Trader, TraderFeatures } from '../services/traderService';
import { ChevronDown, ChevronUp } from 'lucide-react';
import Footer from '../components/Footer';

interface CategoryGroup {
  category: string;
  traders: Trader[];
}

export default function TradesDirectory() {
  const [searchParams] = useSearchParams();
  const [traders, setTraders] = useState<Trader[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [expandedCategories, setExpandedCategories] = useState<string[]>([]);
  const [selectedTrader, setSelectedTrader] = useState<(Trader & { trader_features?: TraderFeatures }) | null>(null);

  const searchQuery = searchParams.get('search') || '';
  const locationQuery = searchParams.get('location') || '';

  useEffect(() => {
    const fetchTraders = async () => {
      setLoading(true);
      try {
        const data = await traderService.getAllTraders();
        console.log('Loaded traders:', data); // Debug log
        setTraders(data || []);
      } catch (error) {
        console.error('Error fetching traders:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTraders();
  }, []);

  // Group traders by category
  const groupedTraders = React.useMemo(() => {
    console.log('Filtering traders:', traders.length); // Debug log
    console.log('Selected category:', selectedCategory); // Debug log

    // First filter traders by selected category if not 'All'
    const filteredTraders = traders.filter(trader => {
      const matchesCategory = selectedCategory === 'All' || 
                            trader.profession?.category === selectedCategory;
      const matchesSearch = !searchQuery || 
        trader.store_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        trader.description.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesLocation = !locationQuery ||
        trader.location.toLowerCase().includes(locationQuery.toLowerCase());

      return matchesCategory && matchesSearch && matchesLocation;
    });

    console.log('Filtered traders:', filteredTraders.length); // Debug log

    const groups: CategoryGroup[] = [];
    const categoriesMap = new Map<string, Trader[]>();

    // Only group the filtered traders
    filteredTraders.forEach(trader => {
      const category = trader.profession?.category || 'Uncategorized';
      if (!categoriesMap.has(category)) {
        categoriesMap.set(category, []);
      }
      categoriesMap.get(category)!.push(trader);
    });

    // Convert map to array and sort categories
    Array.from(categoriesMap.entries()).forEach(([category, traders]) => {
      groups.push({ category, traders });
    });

    // Sort groups by category name
    groups.sort((a, b) => a.category.localeCompare(b.category));
    console.log('Grouped traders:', groups); // Debug log
    return groups;
  }, [traders, selectedCategory, searchQuery, locationQuery]);

  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => 
      prev.includes(category)
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const handleTraderSelect = async (trader: Trader) => {
    try {
      // Fetch full trader details including features
      const fullTraderDetails = await traderService.getTraderById(trader.id || '');
      if (fullTraderDetails) {
        setSelectedTrader(fullTraderDetails);
      }
    } catch (error) {
      console.error('Error fetching trader details:', error);
      // Fallback to original trader if fetching fails
      setSelectedTrader(trader);
    }
  };

  if (selectedTrader) {
    return (
      <TraderDetail
        trader={selectedTrader}
        onBack={() => setSelectedTrader(null)}
      />
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="relative bg-white">
        {/* Overlay Image */}
        <div 
          className="absolute inset-0 bg-center bg-no-repeat opacity-10"
          style={{
            backgroundImage: 'url(/hero-overlay-mainstreet-market.png)',
            backgroundSize: 'cover'
          }}
        />
        <Hero 
          title="Find Local Traders"
          subtitle="Connect with skilled traders in your area"
        />
      </div>

      <div className="mt-8">
        <CategoryFilter
          selectedCategory={selectedCategory}
          onCategoryChange={setSelectedCategory}
        />
      </div>

      <div className="mt-8">
        {loading ? (
          <div className="flex justify-center items-center min-h-[200px]">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
          </div>
        ) : (
          <div className="space-y-12">
            {groupedTraders.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500 text-lg">No traders found.</p>
              </div>
            ) : (
              groupedTraders.map(({ category, traders: categoryTraders }) => (
                <div key={category} className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h2 className="text-2xl font-bold text-gray-900">{category}</h2>
                    {categoryTraders.length > 3 && (
                      <button
                        onClick={() => toggleCategory(category)}
                        className="flex items-center text-blue-600 hover:text-blue-800"
                      >
                        {expandedCategories.includes(category) ? (
                          <>
                            Show Less
                            <ChevronUp className="ml-1 w-5 h-5" />
                          </>
                        ) : (
                          <>
                            Show More
                            <ChevronDown className="ml-1 w-5 h-5" />
                          </>
                        )}
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
                    {categoryTraders
                      .slice(0, expandedCategories.includes(category) ? undefined : 3)
                      .map((trader) => (
                        <TraderCard
                          key={trader.id}
                          trader={trader}
                          onClick={() => handleTraderSelect(trader)}
                        />
                      ))}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}